import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Menu, Plus, ChevronDown, LogOut, Settings as SettingsIcon } from "lucide-react";
import useRequest from "../../hook/use-req";
import { UserResponse } from "../../types";

// Suite-specific top bar for Convo CXO, replacing the generic ConvoHub
// welcome-pill TopNav for this area only (per the new "CXO Dashboard /
// Brighter Insights, Bigger Possibilities" header design). Per explicit
// instruction this now matches the reference design's own brand-yellow bar
// literally — a deliberate, CXO-specific exception to the rest of the app's
// neutral white-label chrome (there's still no real per-org theme-color
// system — see the primary-color-undefined note — so this is a hardcoded
// literal color for this one suite, not a new theming mechanism).
export const CxoTopBar: React.FC<{ onMenuClick?: () => void }> = ({ onMenuClick }) => {
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const [user, setUser] = useState<UserResponse | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { makeRequest: getUserData } = useRequest(`/auth/get_userdata`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getUserData();
      setUser(response);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userDetails");
    localStorage.removeItem("resetOtpCode");
    localStorage.removeItem("role");
    navigate("/");
  };

  const fullName = [user?.payload?.first_name, user?.payload?.last_name].filter(Boolean).join(" ") || "—";

  return (
    <header className="sticky top-0 z-20 mb-3 flex items-center justify-between gap-3 rounded-lg bg-amber-400 px-4 py-3">
      <div className="flex items-center gap-3 min-w-0">
        {onMenuClick && (
          <button
            onClick={onMenuClick}
            aria-label="Toggle menu"
            className="md:hidden shrink-0 p-2 -ml-1 rounded-lg text-gray-900 hover:bg-black/10 transition-colors"
          >
            <Menu size={20} />
          </button>
        )}
        <img className="w-11 h-11 rounded-full shrink-0 object-cover border-2 border-white" src={user?.payload?.org_logo || "/default-logo.png"} alt="logo" />
        <div className="min-w-0">
          <h1 className="text-sm sm:text-base font-semibold text-gray-900 leading-tight truncate">CXO Dashboard</h1>
          <p className="text-[11px] text-gray-800/70 leading-tight truncate hidden sm:block">Brighter Insights, Bigger Possibilities</p>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={() => toast("Custom widgets aren't available yet.")}
          className="flex items-center gap-1.5 bg-gray-900 text-white rounded-full pl-3 pr-3.5 py-2 text-[13px] font-medium hover:opacity-90 transition-opacity"
        >
          <Plus size={14} />
          <span className="hidden sm:inline">New Widget</span>
        </button>

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="flex items-center gap-2 bg-gray-900 text-white rounded-full pl-1.5 pr-2.5 py-1.5 text-[13px] font-medium hover:opacity-90 transition-opacity"
          >
            <img className="w-6 h-6 rounded-full shrink-0 object-cover" src={user?.payload?.org_logo || "/default-logo.png"} alt="logo" />
            <span className="hidden sm:inline truncate max-w-[110px]">{fullName}</span>
            <ChevronDown size={14} className={`shrink-0 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 z-30 mt-1 w-44 overflow-hidden rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1">
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/settings");
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 bg-transparent border-none cursor-pointer text-left"
              >
                <SettingsIcon className="w-4 h-4 shrink-0" />
                Settings
              </button>
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 bg-transparent border-none cursor-pointer text-left"
              >
                <LogOut className="w-4 h-4 shrink-0" />
                Log Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default CxoTopBar;
